import {
  SHContainer,
  SHStack,
  SHTabs,
  SHTypography,
} from "@components/design-systems";
import SHSkeleton from "@components/design-systems/sh-skeleton";
import { UserType } from "@models/auth";
import { InvestmentProductGroup } from "@models/product/investment-product/enums/investmentProductGroup";
import { SupplierUsersGroup } from "@models/users/group";
import { ProductBeingKickedOutDialog } from "@pages/platform-profiles/components/dialog-being-kick-out";
import { ConfirmKickOutDialog } from "@pages/platform-profiles/components/dialog-confirm-kick-out";
import { ConfirmKickOutAdminDialog } from "@pages/platform-profiles/components/dialog-kick-out-admin";
import {
  updateInvestmentProductManagerData,
  updateInvestmentProductModelsData,
  updateInvestmentProductSeriesData,
  updateSelectedTab,
} from "@redux/slices/product/investment-product";
import { RootState, useAppDispatch } from "@redux/store";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { ViewAvailabilityTab } from "./availability";
import { Banner } from "./components/banner";
import { ViewESGTab } from "./esg";
import { FindBDMTab } from "./find-bdm-tab";
import { ViewManagerTab } from "./manager";
import { ViewModelsTab } from "./models";
import { ViewSeriesTab } from "./series";
import { useSearchParams } from "react-router-dom";
import { getViewInvestmentProductTabIndex } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/util";

interface ViewInvestmentProductProps {}

export const ViewInvestmentProduct = (props: ViewInvestmentProductProps) => {
  const { supplierId } = useParams();
  const { investmentProductId } = useParams();
  const {
    investmentProductViewTabs,
    investmentProductUI: {
      selectedTab,
      productType
    },
  } = useSelector((state: RootState) => state.investmentProduct);
  const { user } = useSelector((state: RootState) => state.auth);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const isOtherSupplierUser = useMemo(() => {
    if (!user?.userType) return;
    return (
      SupplierUsersGroup.includes(user?.userType) &&
      user?.userMetadata?.supplier_id !== supplierId
    );

    // eslint-disable-next-line
  }, [supplierId]);

  const targetViewTabIndex = useMemo(() => {
    const tab = searchParams.get("tab");
    if (!tab) return 0;
    return getViewInvestmentProductTabIndex(tab);
  }, [searchParams]);

  const renderTabs = () => {
    if (!investmentProductViewTabs?.length) return [];
    return [
      ...investmentProductViewTabs.map((group) => {
        let component: JSX.Element = <></>;
        switch (group?.name) {
          case "Manager":
            component = <ViewManagerTab />;
            break;
          case "Series":
            component = <ViewSeriesTab />;
            break;
          case "Models":
            component = <ViewModelsTab />;
            break;
          case "Availability":
            component = <ViewAvailabilityTab />;
            break;
          case "ESG":
            component = <ViewESGTab />;
            break;
        }
        return {
          label: group.name,
          component: component,
        };
      }),
      {
        label: "Find BDM",
        component: <FindBDMTab />,
      },
    ];
  };

  const group = investmentProductViewTabs[selectedTab];

  useEffect(() => {
    if (
      user?.userType === UserType.AdviserTrial ||
      user?.userType === UserType.AdviserAdminTrial
    ) {
      if (
        group.name === InvestmentProductGroup.Managed ||
        group.name === InvestmentProductGroup.Series ||
        group.name === InvestmentProductGroup.ESG ||
        group.name === InvestmentProductGroup.Models
      ) {
        dispatch(updateInvestmentProductManagerData(undefined));
        dispatch(updateInvestmentProductSeriesData(undefined));
        dispatch(updateInvestmentProductModelsData(undefined));
        return;
      }
    }

    // eslint-disable-next-line
  }, [investmentProductId]);

  return (
    <SHContainer sx={{ px: { xs: "16px", lg: 0 } }}>
      <SHStack gap={"25px"}>
        <SHStack alignItems={"center"} maxHeight={400}>
          <Banner isOtherSupplierUser={isOtherSupplierUser} />
        </SHStack>
        <SHTypography variant="h2" fontSize={"24px"} fontWeight={600}>
          {!investmentProductViewTabs ? (
            <SHSkeleton height={38} width={"30%"} />
          ) : (
            "Managed account details"
          )}
        </SHTypography>
        <SHTabs
          tabs={renderTabs()}
          onValueChange={(value: string) => {
            dispatch(updateSelectedTab({ selectedTab: +value }));
          }}
          value={targetViewTabIndex.toString()}
        />
        <ConfirmKickOutAdminDialog
          supplierId={supplierId}
          productId={investmentProductId}
          productType={productType}
        />
        <ConfirmKickOutDialog
          supplierId={supplierId}
          productId={investmentProductId}
          productType={productType}
        />
        <ProductBeingKickedOutDialog productType={productType} />
      </SHStack>
    </SHContainer>
  );
};
